/* eslint-disable no-plusplus */
import { useState, useRef, useEffect, useCallback } from "react";
import { useNotification } from "@hooks/useNotifications";
import { translationService } from "features/editor/services/translation";
import { validateItem } from "features/editor/utils/itemValidation";
import { processEditorTextToHtml, cleanTail } from "features/editor/utils/utils";

import type { ITranslationUpdateParams, IValidationError, IMacro } from "features/editor/types";
import type { Language, ValidationRules } from "features/languages/types";
import type { ValidationTemplate } from "features/languages/types/validation-templates";

const normalizeText = (text: string) => {
  let result = text.replace(/\u00A0/g, " ");

  result = cleanTail(result);

  return result;
};

export const useUpdateTranslation = ({
  id,
  originalId,
  index,
  contentEditableRef,
  originalText,
  originalMacros,
  isApproved,
  saveCursorPosition,
  removeCursorPosition,
  languageTranslation,
  validationTemplate,
  validationRules,
  setListItemErrors,
  setListItem,
  syncApprovedCount,
}: {
  id: number;
  originalId: number;
  index: number;
  translationText: string;
  contentEditableRef: React.RefObject<HTMLDivElement>;
  originalText: string;
  originalMacros: Map<string, IMacro>;
  isApproved: boolean;
  saveCursorPosition: () => void;
  removeCursorPosition: () => void;
  languageTranslation: Language;
  validationTemplate: ValidationTemplate;
  validationRules: ValidationRules;
  setListItemErrors: (index: number, errors: IValidationError[]) => void;
  setListItemApprove?: (index: number, isApproved: boolean) => void;
  setListItem: (index: number, log: string, isApproved: boolean) => void;
  syncApprovedCount: (data: ITranslationUpdateParams) => void;
}) => {
  const { showError, showSuccess } = useNotification();
  const isApprovedBtnClickedRef = useRef<boolean>(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [prevValue, setPrevValue] = useState<string>();
  const [isFocused, setIsFocused] = useState(false);
  const timeoutRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const isJobStatusProcessing = useRef<boolean>(false);

  // Validate translation
  const validateTranslation = useCallback(
    (text: string) => {
      const errors = validateItem({
        text,
        originalText,
        languageTranslation,
        validationTemplate,
        validationRules,
      });
      setListItemErrors(index, errors);

      return errors;
    },
    [languageTranslation],
  );

  // Init prevValue
  useEffect(() => {
    const value = contentEditableRef.current?.innerText ?? "";
    validateTranslation(value);
    setPrevValue(value);
  }, []);

  // Save translation
  const updateTranslation = useCallback(
    async (text: string, isApprovedValue: boolean) => {
      if (!isApprovedValue && prevValue === text) {
        return;
      }

      removeCursorPosition();
      setIsUpdating(true);

      try {
        const data: ITranslationUpdateParams = {
          id,
          originalId,
          text,
          isApproved: isApprovedValue,
        };

        const response = await translationService.saveTranslation(data);

        if (response?.isValid === false && data.isApproved) {
          // Check if user is trying to approve invalid record.
          // It works in buggy case if front and back validation rules are different.
          const message = `Failed to save translation. \nRecord is invalid: \n${response.errors.reduce((acc, error) => acc + error.errorMessage, "")}. \n\nPlease contact your administrator.`;
          showError(message);
        } else {
          showSuccess("Successfully saved.");
          syncApprovedCount(data);
          setPrevValue(text);
          setListItem(index, text, isApprovedValue);
        }
      } catch (error) {
        console.error(error);
        showError(error.message ?? "Failed to save translation");
      }

      setIsUpdating(false);
    },
    [prevValue, isApproved],
  );

  // Handle Approve
  const onApproveHandle = useCallback(async () => {
    if (isApproved) {
      return;
    }

    // If timeout is set, it means that the approved flag will be set on blur with text.
    const willBeApprovedOnBlur = Boolean(timeoutRef.current);

    if (willBeApprovedOnBlur) {
      isApprovedBtnClickedRef.current = true;
    } else {
      updateTranslation(prevValue!, true);
    }
  }, [isApproved, prevValue]);

  const saveItem = useCallback(() => {
    const innerText = contentEditableRef?.current?.innerText ?? "";
    const value = normalizeText(innerText);

    // Update editor html, if value was modified
    if (innerText !== value) {
      const html = processEditorTextToHtml(value, originalMacros);
      const contentEditable = contentEditableRef.current;

      if (contentEditable) {
        contentEditable.innerHTML = html;
      }
    }

    // By default text change set approved to false.
    // But there is a case when the user clicks the approve button and the text is on focus.
    const isApprovedValue = Boolean(isApprovedBtnClickedRef.current);

    const errors = validateTranslation(value);
    updateTranslation(value, isApprovedValue && !errors.length);

    if (isApprovedValue && errors.length) {
      showError("The item was not approved. Please check the validation errors.");
    }
  }, [prevValue, isApproved]);

  useEffect(() => {
    // Focus
    const handleFocus = () => {
      // Clear the timeout if the element regains focus
      clearTimeout(timeoutRef.current!);
      setIsFocused(true);
    };

    // Blur
    const handleBlur = () => {
      if (isJobStatusProcessing.current) {
        showError("The last item was not saved properly.\n Database synchronization has been started.", undefined, {
          isDialog: false,
        });
        return;
      }

      saveCursorPosition();

      // Start a timeout when the element loses focus
      timeoutRef.current = setTimeout(() => {
        // If the element is still not focused after timeout, call the callback
        if (!isFocused && contentEditableRef.current) {
          saveItem();
        }

        timeoutRef.current = null;
        isApprovedBtnClickedRef.current = false;
      }, window.appConfig.ui.editor.saveTimeout);
      setIsFocused(false);
    };

    // Event 'setTranslationValue'
    const setTranslationValue = (event: CustomEvent<{ value: string }>) => {
      const { value } = event.detail;
      const html = processEditorTextToHtml(value, originalMacros);

      if (contentEditableRef?.current) {
        // eslint-disable-next-line no-param-reassign
        contentEditableRef.current.innerHTML = html;

        saveItem();
      }
    };

    contentEditableRef.current?.addEventListener("focus", handleFocus);
    contentEditableRef.current?.addEventListener("blur", handleBlur);
    contentEditableRef.current?.addEventListener("setTranslationValue", setTranslationValue as EventListener);

    return () => {
      contentEditableRef.current?.removeEventListener("focus", handleFocus);
      contentEditableRef.current?.removeEventListener("blur", handleBlur);
      contentEditableRef.current?.removeEventListener("setTranslationValue", setTranslationValue as EventListener);
    };
  }, [prevValue, languageTranslation]);

  // Handle In Progress Overlay
  const handleInProgressOverlay = useCallback(() => {
    isJobStatusProcessing.current = true;

    if (isFocused && contentEditableRef?.current) {
      contentEditableRef.current.blur();
    }
  }, [isFocused]);

  useEffect(() => {
    document.addEventListener("job-status:in-progress", handleInProgressOverlay);

    return () => {
      document.removeEventListener("job-status:in-progress", handleInProgressOverlay);
    };
  });

  return { isUpdating, onApproveHandle };
};
